import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { VendorTrackingDetails } from '../services/vendor/types';
import { ExtensionBanner } from './index';

interface ExtensionContextType {
    activatePromo: () => void;
    deactivatePromo: () => void;
    openDeepLink: (trackingDetails: VendorTrackingDetails) => void;
}

const ExtensionContext = createContext<ExtensionContextType>({
    activatePromo: () => {},
    deactivatePromo: () => {},
    openDeepLink: (_trackingDetails: VendorTrackingDetails) => {}
});

const MESSAGE_SOURCE = 'theezy-extension';

type ExtensionPingMessage = {
    source: typeof MESSAGE_SOURCE,
    type: string
}

const ExtensionContextProvider =  ({ children }: { children: React.ReactNode }) => {
    const [showExtensionPromo, setShowExtensionPromo] = useState<boolean | undefined>();
    const [activate, setActivate] = useState<boolean>(false);

    useEffect(() => {
        const handler = (event: MessageEvent<ExtensionPingMessage>) => {
            if (
                event.origin === window.location.origin &&
                event.data?.source === MESSAGE_SOURCE &&
                event.data?.type === 'EXTENSION_PING'
            ) {
                setShowExtensionPromo(false);
            }
        };

        window.addEventListener('message', handler);

        // secondary ping check in case, extension sneds first message before this useEffect runs
        window.postMessage({ type: 'EXTENSION_CHECK', source: 'theezy-webapp' }, window.location.origin);

        const timeoutId = setTimeout(() => {
            setShowExtensionPromo(prev => prev === undefined ? true : prev);
        }, 3000);

        return () => {
            window.removeEventListener('message', handler);
            clearTimeout(timeoutId);
        };
    }, []);

    // Locally this popup will always trigger if the local extension isn't
    // properly set up for Localhost communication
    const activatePromo = useCallback(() => {
        setActivate(true);
    }, [])

    const deactivatePromo = useCallback(() => {
        setActivate(false);
    }, [])

    const openDeepLink = useCallback((trackingDetails: VendorTrackingDetails)=> {
        window.postMessage(
            {
                type: 'DEEPLINK_OPEN',
                url: trackingDetails.url,
                period: trackingDetails.period,
            },
            window.location.origin
        )
        window.open(trackingDetails.url, '_blank')
    }, []);

    const value = useMemo(() => ({
        activatePromo,
        deactivatePromo,
        openDeepLink
    }), [activatePromo, deactivatePromo, openDeepLink]);

    return (
        <ExtensionContext.Provider value={value}>
            <ExtensionBanner activate={activate} showExtensionPromo={showExtensionPromo}/>
            {children}
        </ExtensionContext.Provider>
    );
}

const useExtension = () => useContext(ExtensionContext);

export { useExtension, ExtensionContextProvider };